import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Loader2, Upload, Inbox, CheckCircle2, Briefcase, GraduationCap, HardHat, FileText } from "lucide-react";

const SECTIONS = [
  { key: "rrhh", label: "RRHH", icon: Briefcase },
  { key: "formacion", label: "Formación", icon: GraduationCap },
  { key: "prl_epis", label: "PRL y EPIs", icon: HardHat },
];

const dl = async (id, name) => {
  try {
    const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.download = name || "documento"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar"); }
};

export default function EmployeeDocuments() {
  const [docs, setDocs] = useState({ rrhh: [], formacion: [], prl_epis: [] });
  const [requests, setRequests] = useState([]);
  const [uploading, setUploading] = useState(null);

  const load = useCallback(async () => {
    const results = await Promise.all(SECTIONS.map((s) => api.get(`/me/documents?section=${s.key}`).then((r) => r.data).catch(() => [])));
    const map = {}; SECTIONS.forEach((s, i) => (map[s.key] = results[i]));
    setDocs(map);
    api.get("/me/document-requests").then(({ data }) => setRequests(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const ack = async (id) => { await api.post(`/documents/${id}/ack`); toast.success("Recepción confirmada"); load(); };

  const fulfill = async (reqId, file) => {
    if (!file) return;
    setUploading(reqId);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/me/documents/${reqId}/fulfill`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Documento enviado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(null); }
  };

  return (
    <div className="space-y-5">
      {requests.length > 0 && (
        <Card className="p-4 border-sky-200 bg-sky-50/60 shadow-none" data-testid="doc-requests-panel">
          <div className="flex items-center gap-2 font-semibold text-sky-800 mb-3"><Inbox className="h-4 w-4" /> Documentos solicitados</div>
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2" data-testid={`my-req-${r.id}`}>
                <span className="text-sm text-slate-700">{r.title}</span>
                <label className="cursor-pointer">
                  <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx" onChange={(e) => fulfill(r.id, e.target.files[0])} data-testid={`fulfill-${r.id}`} />
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-sky-600 hover:text-sky-700">
                    {uploading === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Subir
                  </span>
                </label>
              </div>
            ))}
          </div>
        </Card>
      )}

      {SECTIONS.map((s) => (
        <div key={s.key}>
          <h3 className="font-heading font-semibold flex items-center gap-2 mb-2 text-slate-700"><s.icon className="h-4 w-4 text-slate-400" /> {s.label}</h3>
          <div className="space-y-2">
            {docs[s.key]?.map((d) => (
              <Card key={d.id} className="p-3.5 border-slate-200 shadow-none flex items-center justify-between" data-testid={`my-doc-${d.id}`}>
                <div>
                  <div className="font-medium text-slate-800 text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-300" /> {d.title}
                    {d.requires_ack && (d.acknowledged
                      ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] gap-1"><CheckCircle2 className="h-3 w-3" /> Recibido</Badge>
                      : <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Confirma recepción</Badge>)}
                  </div>
                  <div className="text-xs text-slate-400 ml-6">{d.issue_date || "—"}{d.expiry_date && ` · caduca ${d.expiry_date}`}{d.item && ` · ${d.item}`}</div>
                </div>
                <div className="flex gap-1">
                  {d.requires_ack && !d.acknowledged && <Button size="sm" variant="outline" onClick={() => ack(d.id)} data-testid={`ack-${d.id}`}>Confirmar</Button>}
                  {d.file_path && <Button size="icon" variant="ghost" onClick={() => dl(d.id, d.file_name)} data-testid={`dl-${d.id}`}><Download className="h-4 w-4" /></Button>}
                </div>
              </Card>
            ))}
            {!docs[s.key]?.length && <div className="text-sm text-slate-400 py-2">Sin documentos.</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
